<script setup>
import cdnFile from '@/helpers/cdnFile.js'
import formatMessage from '@/helpers/formatMessage.js'
import { generateSrcSet } from '@/helpers/srcSetHelper'
import SvgIcon from '@/components/SvgIcon.vue'

const props = defineProps({
  displayModel: {
    type: Object,
    required: true,
  },
  selectedModelId: {
    type: [String, Number],
    required: false,
    default: null,
  },
  user: {
    type: Object,
    required: false,
    default: null,
  },
  canDeleteModel: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['select', 'clear', 'delete', 'togglePin'])

function onModelClick() {
  emit('select', props.displayModel)
}

function onClearModel() {
  emit('clear', props.displayModel)
}

function onDeleteModel() {
  emit('delete', props.displayModel)
}

function onTogglePin() {
  emit('togglePin', props.displayModel)
}
</script>

<template>
  <div
    class="flex gap-[15px] items-center bg-[#0E122A] mx-3 px-[15px] py-[21px] cursor-pointer min-h-14 rounded-[15px]"
    :class="{
      'border border-[#B651FF]': selectedModelId === displayModel.id,
    }"
    :style="
      selectedModelId === displayModel.id
        ? { 'box-shadow': '0px 0px 0px 3px #B552FF26' }
        : {}
    "
    @click="onModelClick"
  >
    <div class="h-[42px] w-[42px] rounded-full overflow-hidden shrink-0">
      <img
        v-if="
          displayModel.premadeId &&
          !displayModel.profilePicture?.startsWith('sd/')
        "
        class="w-full rounded-xl aspect-[832/1216] no-image-select"
        :src="generateSrcSet('models', displayModel.name, 'webp', 'xs', true)"
        :alt="displayModel.name"
        draggable="false"
      />
      <img
        v-else
        class="w-full rounded-xl aspect-[832/1216] no-image-select"
        :src="
          cdnFile(displayModel.profilePicture || displayModel.referenceImage)
        "
        :alt="displayModel.name"
        draggable="false"
      />
    </div>
    <div class="flex flex-col truncate w-full">
      <div class="flex items-center">
        <p class="text-[15px]">
          {{ displayModel.name }}
        </p>
        <button
          v-if="user?.subscription"
          @click.stop="onTogglePin"
          class="flex justify-center items-center bg-[#11152D] p-1.5 my-[1px] rounded-full"
        >
          <SvgIcon
            v-if="!displayModel.pinned"
            icon-id="push-pin-outline"
            class="w-3.5 h-3.5"
          />
          <SvgIcon v-else icon-id="push-pin" class="w-3.5 h-3.5" />
        </button>
      </div>
      <div
        class="text-[13px] max-w-[145px] text-[#B1B5DB] truncate"
        v-html="
          displayModel?.messages?.[0]?.imageDescription
            ? 'Sent a picture'
            : formatMessage(displayModel?.messages?.[0]?.content)
        "
      ></div>
    </div>
    <div class="flex items-end pb-1 justify-center h-full gap-[9px]">
      <button
        v-if="user?.subscription"
        class="flex justify-center items-center bg-[#11152D] w-[35px] h-[35px] rounded-full"
        style="
          box-shadow:
            0px 0px 0px 1.2px #1e1a3d,
            0px 0px 12px 2.4px #13142e inset;
        "
        @click.stop="onClearModel"
      >
        <SvgIcon icon-id="broom" class="w-[21px] h-[21px]" />
      </button>
      <button
        v-if="canDeleteModel"
        class="flex justify-center items-center bg-[#11152D] w-[35px] h-[35px] rounded-full"
        style="
          box-shadow:
            0px 0px 0px 1.2px #1e1a3d,
            0px 0px 12px 2.4px #13142e inset;
        "
        @click.stop="onDeleteModel"
      >
        <SvgIcon icon-id="trash" class="w-[21px] h-[21px]" />
      </button>
    </div>
  </div>
</template>
